import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCount } from '../utils/numberFormat';

// ─── Asset paths (public/shareprofile/ → served at /shareprofile/*) ──────────
const SP = (name: string) => `/shareprofile/${name}`;

const BG_SRCS: Record<string, string> = {
  green:  SP('greenbackground.jpg'),
  purple: SP('purplebackground.jpg'),
  blue:   SP('bluebackground.jpg'),
  orange: SP('orangebackground.jpg'),
  pink:   SP('pinkbackground.jpg'),
  red:    SP('redbackground.jpg'),
  yellow: SP('yellowbackground.jpg'),
};

// Exact filenames from assets/shareprofile/
const AFROYAPPER_SRC      = SP('afroyapper.png');
const FONT_KUFFNER_SRC    = SP('BaronessKuffner.ttf');   // exact filename
const FONT_CAPTAIN_SRC    = SP('AmericanCaptain.ttf');   // exact filename

// ─── Background swatches ─────────────────────────────────────────────────────
const BACKGROUNDS = [
  { id: 'green',  label: 'Green',  swatch: '#2d6a30' },
  { id: 'purple', label: 'Purple', swatch: '#5a2d82' },
  { id: 'blue',   label: 'Blue',   swatch: '#1a3a7a' },
  { id: 'orange', label: 'Orange', swatch: '#c44a00' },
  { id: 'pink',   label: 'Pink',   swatch: '#b0205a' },
  { id: 'red',    label: 'Red',    swatch: '#b01a1a' },
  { id: 'yellow', label: 'Yellow', swatch: '#b8960a' },
] as const;

type BgId = typeof BACKGROUNDS[number]['id'];

// ─── Card dimensions ──────────────────────────────────────────────────────────
const CARD_W = 800;
const CARD_H = 1040;

// ─── Left-strip metrics (the @username column) ────────────────────────────────
// The left strip spans x: 0–220. Everything is centered at STRIP_CX.
const STRIP_CX = 110; // horizontal center of the @username column

// ─── Photo slot layout base dimensions ──────────────────────────────────────────
const SLOT_W = 270;
const SLOT_H = 370;

// ─── Types ────────────────────────────────────────────────────────────────────
interface ShareProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  handle?: string;
  userId?: string;
  yapsCount?: number;
  friendsCount?: number;
  portfolio?: string[];
  portfolioPreviews?: string[];
  customLink?: string;
  linkButtonText?: string;
}

// ─── Font loader ──────────────────────────────────────────────────────────────
let _fontsLoaded = false;
async function loadFonts(): Promise<void> {
  if (_fontsLoaded) return;
  await Promise.allSettled([
    new FontFace('BaronessKuffner', `url("${FONT_KUFFNER_SRC}")`).load().then(f => {
      (document.fonts as FontFaceSet).add(f);
    }),
    new FontFace('AmericanCaptain', `url("${FONT_CAPTAIN_SRC}")`).load().then(f => {
      (document.fonts as FontFaceSet).add(f);
    }),
  ]);
  _fontsLoaded = true;
}

// Cache for loaded images to prevent lag on color swaps
interface CacheEntry {
  img: HTMLImageElement | HTMLVideoElement;
  src: string;
  isTainted: boolean;
}

// Convert a blob to a Data URL (most reliable way to avoid tainting)
function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Detect if a URL is likely a video based on extension
function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const cleanUrl = url.split('?')[0].toLowerCase();
  return cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.mov') || cleanUrl.endsWith('.m4v') || cleanUrl.endsWith('.webm');
}

// Extract a frame from a video safely
function loadVideoSafe(
  src: string,
  cacheRef: React.MutableRefObject<Record<string, CacheEntry>>
): Promise<CacheEntry | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    // Use a proxy for video files too to ensure CORS is satisfied for canvas
    const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(src)}`;
    
    video.onloadeddata = () => {
      // Seek to 0.1s to get a valid frame (standard practice)
      video.currentTime = 0.1;
    };
    
    video.onseeked = () => {
      const entry = { img: video, src, isTainted: false };
      cacheRef.current[src] = entry;
      resolve(entry);
    };
    
    video.onerror = () => {
      console.warn('[ShareProfile] Video load failed, trying tainted fallback:', src);
      const fallback = document.createElement('video');
      fallback.onloadeddata = () => {
        fallback.currentTime = 0.1;
      };
      fallback.onseeked = () => {
        const entry = { img: fallback, src, isTainted: true };
        cacheRef.current[src] = entry;
        resolve(entry);
      };
      fallback.onerror = () => resolve(null);
      fallback.src = src;
    };
    
    video.src = proxyUrl;
    video.load();
  });
}

// Global-ish cache within the component scope (re-initialized on mount)
async function loadImgSafe(
  src: string, 
  cacheRef: React.MutableRefObject<Record<string, CacheEntry>>
): Promise<CacheEntry | null> {
  if (!src) return null;
  if (cacheRef.current[src]) return cacheRef.current[src];

  // 1. Handle Videos
  if (isVideoUrl(src)) {
    return loadVideoSafe(src, cacheRef);
  }

  const tryLoad = (url: string, crossOrigin?: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = crossOrigin;
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${url}`));
      img.src = url;
    });
  };

  // 2. Same-origin or relative paths (Always safe)
  if (!src.startsWith('http')) {
    try {
      const img = await tryLoad(src);
      const entry = { img, src, isTainted: false };
      cacheRef.current[src] = entry;
      return entry;
    } catch {
      return null;
    }
  }

  // 3. Cross-origin approach with Fetch-to-Blob (Ultimate bypass)
  // This is much more reliable than Image.crossOrigin for Firebase
  const tryFetchToBlob = async (url: string): Promise<CacheEntry | null> => {
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('Fetch failed');
      const blob = await resp.blob();
      const dataUrl = await blobToDataURL(blob);
      const img = await tryLoad(dataUrl); // DataURLs same-origin
      const entry = { img, src, isTainted: false };
      cacheRef.current[src] = entry;
      return entry;
    } catch {
      return null;
    }
  };

  // Layer A: Direct Fetch
  let entry = await tryFetchToBlob(src);
  if (entry) return entry;

  // Layer B: Weserv Proxy (Professional image proxy)
  try {
    const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(src)}&output=jpg&q=95`;
    const img = await tryLoad(proxyUrl, 'anonymous');
    entry = { img, src, isTainted: false };
    cacheRef.current[src] = entry;
    return entry;
  } catch {
    // Layer C: AllOrigins Proxy Fetch (General CORS bypass)
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(src)}`;
    entry = await tryFetchToBlob(proxyUrl);
    if (entry) return entry;

    // Layer D: Tainted fallback (Visual only)
    console.warn('[ShareProfile] All secure paths failed, falling back to tainted load:', src);
    try {
      const img = await tryLoad(src);
      entry = { img, src, isTainted: true };
      cacheRef.current[src] = entry;
      return entry;
    } catch {
      return null;
    }
  }
}

// Load a public asset (fonts/branding)
function loadPublicImg(src: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // satisfy canvas security
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// ─── Canvas helpers ───────────────────────────────────────────────────────────
function drawFillCenter(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | HTMLVideoElement,
  dx: number, dy: number, dw: number, dh: number,
) {
  const iw = (img instanceof HTMLVideoElement) ? img.videoWidth : (img.naturalWidth || img.width);
  const ih = (img instanceof HTMLVideoElement) ? img.videoHeight : (img.naturalHeight || img.height);
  const scale = Math.max(dw / iw, dh / ih);
  const sw = dw / scale;
  const sh = dh / scale;
  const sx = (iw - sw) / 2;
  const sy = (ih - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r = 8,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y,       x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h,   x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h,       x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y,           x + r, y);
  ctx.closePath();
}

// Draw a photo slot with drop-shadow + rounded clip
function drawSlot(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | HTMLVideoElement | null,
  x: number, y: number, w: number, h: number,
) {
  // Shadow pass (drawn on solid black rect before clipping to project real opaque shadow)
  ctx.save();
  ctx.shadowColor   = 'rgba(0,0,0,0.8)';
  ctx.shadowBlur    = 25;
  ctx.shadowOffsetX = 6;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle     = '#000000'; // Casts solid opaque shadow
  roundedRect(ctx, x, y, w, h);
  ctx.fill();
  ctx.restore();

  // Photo pass (clipped to rounded rect, no shadow)
  ctx.save();
  roundedRect(ctx, x, y, w, h);
  ctx.clip();
  if (img) {
    drawFillCenter(ctx, img, x, y, w, h);
  } else {
    // Placeholder - Light Grey X as requested
    ctx.fillStyle = 'rgba(0,0,0,0.40)';
    ctx.fillRect(x, y, w, h);
    ctx.font = 'bold 60px "AmericanCaptain", "Impact", sans-serif'; 
    ctx.fillStyle = 'rgba(255,255,255,0.15)'; 
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('X', x + w / 2, y + h / 2);
  }
  ctx.restore();
}

// ─── Main render ──────────────────────────────────────────────────────────────
async function renderCard(
  canvas: HTMLCanvasElement,
  bgSrc: string,
  photos: (HTMLImageElement | HTMLVideoElement | null)[],
  branding: { afroyapper: HTMLImageElement | null },
  handle: string,
  yapsCount: number,
  friendsCount: number,
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  canvas.width  = CARD_W;
  canvas.height = CARD_H;

  // ── Background ────────────────────────────────────────────────────────────
  const bgImg = await loadPublicImg(bgSrc);
  if (bgImg) {
    ctx.drawImage(bgImg, 0, 0, CARD_W, CARD_H);
  } else {
    ctx.fillStyle = '#2d6a30';
    ctx.fillRect(0, 0, CARD_W, CARD_H);
  }

  // ── Portfolio photos (dynamic overlapping layout) ───────────────────────────
  const validPhotos = photos.filter(p => p !== null);
  
  if (validPhotos.length === 1) {
    drawSlot(ctx, validPhotos[0], 250, 150, 460, 630);
  } else if (validPhotos.length === 2) {
    drawSlot(ctx, validPhotos[0], 230, 100, 320, 440);
    drawSlot(ctx, validPhotos[1], 420, 400, 320, 440);
  } else if (validPhotos.length === 3) {
    drawSlot(ctx, validPhotos[0], 230, 80, SLOT_W, SLOT_H);
    drawSlot(ctx, validPhotos[1], 480, 200, SLOT_W, SLOT_H);
    drawSlot(ctx, validPhotos[2], 350, 500, SLOT_W, SLOT_H);
  } else {
    // 4 or 0 (fallback to original 4-slot layout with placeholders if 0)
    const baseSlots = [
      { x: 230, y: 50,  w: SLOT_W, h: SLOT_H }, 
      { x: 480, y: 130, w: SLOT_W, h: SLOT_H }, 
      { x: 350, y: 390, w: SLOT_W, h: SLOT_H }, 
      { x: 490, y: 580, w: SLOT_W, h: SLOT_H }, 
    ];
    for (let i = 0; i < 4; i++) {
      const s = baseSlots[i];
      drawSlot(ctx, photos[i] || null, s.x, s.y, s.w, s.h);
    }
  }

  // ── afroyapper.png — beautifully enlarged mid-ground element ─────────────
  // Drawn before text so if there's any vertical overlap, the text sits brilliantly on top!
  if (branding.afroyapper) {
    const ayW = 820; // Expanded massively to scale up into the empty space!
    const ayH = Math.round(branding.afroyapper.height * (ayW / branding.afroyapper.width));
    // Placed firmly in the bottom left corner as requested!
    const ayX = 10; 
    const ayY = CARD_H - ayH + 5; // Dropped exactly 10px down to nail the perfect anchor point

    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur    = 16;
    ctx.shadowOffsetX = 4;
    ctx.shadowOffsetY = 8;
    ctx.drawImage(branding.afroyapper, ayX, ayY, ayW, ayH);
    ctx.restore();
  }

  // ── Fonts ─────────────────────────────────────────────────────────────────
  const KUF = `"BaronessKuffner", "Impact", sans-serif`;
  const CAP = `"AmericanCaptain", "Impact", sans-serif`;

  // ── Username — large, vertical (rotating +90°) and attached to @ ───────
  const handleText = `@${handle}`;
  ctx.save();
  
  // The username spans the whole left edge independent of sticker positioning
  const availableNameSpace = 900; 
  
  // 📏 FONT SCALING: If the username is long (> 7 chars), we drop the base size
  // to ensure it doesn't collide with the 'Add My Afro Yap' sticker at the bottom.
  let fontSize = handle.length > 7 ? 110 : 145; 
  
  ctx.font = `${fontSize}px ${KUF}`;
  const rawLen = ctx.measureText(handleText).width;
  if (rawLen > availableNameSpace && availableNameSpace > 0) {
    fontSize = Math.floor(fontSize * (availableNameSpace / rawLen));
    ctx.font = `bold ${fontSize}px ${KUF}`;
  }
  const nameLen = ctx.measureText(handleText).width;

  const STRIP_X = 85; // Hugging the left edge
  // Anchor at the top left.
  ctx.translate(STRIP_X, 40);
  ctx.rotate(Math.PI / 2); // +90 degrees ensures bottom of letters faces left border
  
  ctx.fillStyle    = '#ffffff';
  ctx.textAlign    = 'left'; // Proceeds down the Y-axis
  ctx.textBaseline = 'middle';
  ctx.strokeStyle  = '#000000';
  ctx.lineWidth    = 6;
  ctx.lineJoin     = 'round';

  // Add strong drop shadow to the main handle text
  ctx.shadowColor   = 'rgba(0,0,0,0.85)';
  ctx.shadowBlur    = 15;
  ctx.shadowOffsetX = 6;
  ctx.shadowOffsetY = 10;

  ctx.strokeText(handleText, 0, 0);
  ctx.fillText(handleText, 0, 0);
  ctx.restore();



  // ── YAPS stat ─────────────────────────────────────────────────────────────
  // Stats dynamically adapt to username length: hugging closely below short names
  // but strictly bounded from sinking into the afroyapper sticker!
  const numSz  = 65;
  const lblSz  = 50;
  const statsTotalHeight = numSz + 8 + lblSz;
  
  // Detect if the username is "large" based on a strict > 5 character rule
  const isLargeUsername = handleText.length > 5;
  
  // If it's a short username, hug the vertical axis.
  // If it's a massive username, shift right to yield the column so the username isn't blocked!
  const statsX = isLargeUsername ? 175 : 60; 
  
  // If short, float precisely under the username.
  // If large, lock into the visual pocket between the username and the portfolio pictures.
  let statsY = isLargeUsername ? 480 : 40 + nameLen + 90; 
  
  const maxStatsY = 550; 
  statsY = Math.min(statsY, maxStatsY); // Safety floor

  // Yaps number (clean red)
  ctx.save();
  ctx.font         = `bold ${numSz}px ${CAP}`;
  ctx.fillStyle    = '#e63939';
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'top';
  ctx.shadowColor  = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur   = 8;
  const yStr = formatCount(yapsCount);
  ctx.fillText(yStr, statsX, statsY);
  const yW   = ctx.measureText(yStr).width;
  ctx.restore();

  // Yaps label (clean white)
  ctx.save();
  ctx.font         = `bold ${lblSz}px ${CAP}`;
  ctx.fillStyle    = '#ffffff';
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'top';
  ctx.shadowColor  = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur   = 8;
  ctx.shadowOffsetX = 3;
  ctx.shadowOffsetY = 4;
  ctx.fillText(' YAPS', statsX + yW + 2, statsY + 10);
  ctx.restore();

  // Friends number (clean red)
  const friendsY = statsY + numSz + 8;
  ctx.save();
  ctx.font         = `bold ${numSz}px ${CAP}`;
  ctx.fillStyle    = '#e63939';
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'top';
  ctx.shadowColor  = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur   = 8;
  const fStr      = formatCount(friendsCount);
  ctx.fillText(fStr, statsX, friendsY);
  const fW        = ctx.measureText(fStr).width;
  ctx.restore();

  // Friends label (clean white)
  ctx.save();
  ctx.font         = `bold ${lblSz}px ${CAP}`;
  ctx.fillStyle    = '#ffffff';
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'top';
  ctx.shadowColor  = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur   = 8;
  ctx.shadowOffsetX = 3;
  ctx.shadowOffsetY = 4;
  ctx.fillText(' FRIENDS', statsX + fW + 2, friendsY + 10);
  ctx.restore();

}

// ─── Component ────────────────────────────────────────────────────────────────
export function ShareProfileModal({
  isOpen,
  onClose,
  username,
  handle,
  userId,
  yapsCount    = 0,
  friendsCount = 0,
  portfolio    = [],
  portfolioPreviews = [],
  customLink,
  linkButtonText,
}: ShareProfileModalProps) {
  const [selectedBg, setSelectedBg]   = useState<BgId>('green');
  const [isRendering, setIsRendering] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [fontsReady, setFontsReady]   = useState(false);

  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const brandingRef = useRef<{ afroyapper: HTMLImageElement | null }>({
    afroyapper:  null,
  });
  const [brandingReady, setBrandingReady] = useState(false);

  // Performance caching refs
  const imgCacheRef = useRef<Record<string, CacheEntry>>({});
  const photoRefs   = useRef<(HTMLImageElement | HTMLVideoElement | null)[]>([null, null, null, null]);
  const isTaintedRef = useRef(false);
  
  // Handle text — strip leading @, uppercase
  const displayHandle = (handle || username || 'HANDLE').replace(/^@/, '').toUpperCase();

  // ── Load fonts + branding once ────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      // Fonts
      await loadFonts();
      await document.fonts.ready;
      if (active) setFontsReady(true);

      // Branding overlays
      const [afroyapper] = await Promise.all([
        loadPublicImg(AFROYAPPER_SRC),
      ]);
      if (!active) return;
      brandingRef.current = { afroyapper };
      setBrandingReady(true);
    })();
    return () => { active = false; };
  }, []);

  // ── Effective photo URLs (prefer previews) ────────────────────────────────
  const getPhotoUrls = useCallback(() =>
    Array.from({ length: 4 }, (_, i) =>
      (portfolioPreviews[i] && portfolioPreviews[i].length > 0
        ? portfolioPreviews[i]
        : (portfolio[i] || '')),
    ),
    [portfolio, portfolioPreviews],
  );

  // ── Background Pre-loading — Starts as soon as the profile data is available! ─────
  useEffect(() => {
    const urls = getPhotoUrls();
    // Warm up the cache in the background so it's ready before the user even clicks
    urls.forEach(u => {
      if (u) loadImgSafe(u, imgCacheRef);
    });
    // Also warm up all the background variations
    Object.values(BG_SRCS).forEach(src => {
      loadImgSafe(src, imgCacheRef);
    });
  }, [getPhotoUrls]); 

  // ── Render canvas ─────────────────────────────────────────────────────────
  const doRender = useCallback(async () => {
    if (!canvasRef.current || !brandingReady || !fontsReady) return;

    setIsRendering(true);
    // Reset tainting flag for this render cycle
    let sessionTainted = false;

    try {
      const urls = getPhotoUrls();
      
      // Load/Cache images. 
      // Promise.allSettled ensures one failure doesn't hang the whole UI!
      const results = await Promise.allSettled(urls.map(u => loadImgSafe(u, imgCacheRef)));
      const imgs: (HTMLImageElement | HTMLVideoElement | null)[] = results.map(r => {
        if (r.status === 'fulfilled' && r.value) {
          if (r.value.isTainted) sessionTainted = true;
          return r.value.img;
        }
        return null;
      });

      // Load/Cache background
      const bgUri = BG_SRCS[selectedBg];
      const bgEntry = await loadImgSafe(bgUri, imgCacheRef);
      if (bgEntry?.isTainted) sessionTainted = true;

      // Update session state
      isTaintedRef.current = sessionTainted;
      photoRefs.current = imgs;

      // Actual Canvas Draw (Synchronous after images are ready)
      await renderCard(
        canvasRef.current,
        bgUri,
        imgs,
        brandingRef.current,
        displayHandle,
        yapsCount,
        friendsCount,
      );
    } catch (err) {
      console.error('[ShareProfile] Render error:', err);
    } finally {
      setIsRendering(false);
    }
  }, [selectedBg, brandingReady, fontsReady, displayHandle, yapsCount, friendsCount, getPhotoUrls]);

  useEffect(() => {
    if (isOpen && brandingReady && fontsReady) doRender();
  }, [isOpen, doRender, brandingReady, fontsReady, selectedBg, yapsCount, friendsCount]);

  // ── Swap background (instant) ─────────────────────────────────────────────
  const handleBgSelect = (id: BgId) => setSelectedBg(id);

  // ── Export ────────────────────────────────────────────────────────────────
  const exportJpeg = async (): Promise<string> => {
    if (isTaintedRef.current) {
      throw new Error('SECURITY_BLOCK');
    }
    await doRender();
    return canvasRef.current!.toDataURL('image/jpeg', 0.95);
  };

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      const isIOS = Capacitor.getPlatform() === 'ios';
      
      if (isIOS) {
        // On iOS, a standard <a> download saves to the "Files" app.
        // To get it into "Apple Photos", we MUST use the Share Sheet's "Save Image".
        await doRender();
        const blob = await new Promise<Blob>((res, rej) =>
          canvasRef.current!.toBlob(b => (b ? res(b) : rej(new Error('Blob failed'))), 'image/jpeg', 0.95),
        );
        const file = new File([blob], `afroyap-${displayHandle.toLowerCase()}.jpg`, { type: 'image/jpeg' });
        
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ 
            files: [file], 
            title: `Save Profile Card` 
          });
          return;
        }
      }

      // Default Web/Android behavior (saves to downloads folder)
      const url = await exportJpeg();
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `afroyap-${displayHandle.toLowerCase()}.jpg`;
      a.click();
      toast.success('Profile card saved!');
    } catch (err: any) {
      if (err?.message === 'SECURITY_BLOCK') {
        toast.error('Security block', { 
          description: 'One of your photos is restricted. The card is visible but cannot be downloaded.' 
        });
      } else {
        console.error('[ShareProfile] Export failed:', err);
        toast.error('Export failed. Try again in a moment.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async () => {
    setIsExporting(true);
    try {
      await doRender();
      const blob = await new Promise<Blob>((res, rej) =>
        canvasRef.current!.toBlob(b => (b ? res(b) : rej(new Error('Blob failed'))), 'image/jpeg', 0.95),
      );
      const file = new File([blob], `afroyap-${displayHandle.toLowerCase()}.jpg`, { type: 'image/jpeg' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `@${displayHandle} on AfroYap` });
      } else {
        await handleDownload();
        return;
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') toast.error('Share failed');
    } finally {
      setIsExporting(false);
    }
  };

  // Generate a public profile deep-link when userId is available; fall back to App Store.
  const PROFILE_BASE = 'https://afroyap-8bfb1.web.app/p';
  const APP_LINK = "https://apps.apple.com/app/apple-store/id6757343172?pt=128420251&ct=Share%20Profile&mt=8";
  const linkToCopy = customLink || (userId ? `${PROFILE_BASE}/${userId}` : APP_LINK);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkToCopy);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const busy = isRendering || isExporting || !brandingReady || !fontsReady;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 overflow-y-auto no-scale"
          onClick={onClose}
        >
          <div 
            className="min-h-full flex flex-col items-center py-12 px-6"
            style={{
              paddingTop:    'max(env(safe-area-inset-top), 40px)',
              paddingBottom: 'max(env(safe-area-inset-bottom, 0px) + 60px, 80px)',
            }}
          >
            <motion.div
              initial={{ scale: 0.93, opacity: 0, y: 24 }}
              animate={{ scale: 1,    opacity: 1, y: 0  }}
              exit={{ scale: 0.93, opacity: 0, y: 24 }}
              transition={{ type: 'spring', damping: 22, stiffness: 300 }}
              data-tour="guide-share-card"
              className="rounded-3xl overflow-hidden bg-[#111] border border-white/10 shadow-2xl"
              style={{ width: '100%', maxWidth: 460 }}
              onClick={e => e.stopPropagation()}
            >
            {/* ── Header ─────────────────────────────────────────────────── */}
            <div className="relative flex items-center justify-center px-12 pt-6 pb-4">
              <h2 className="text-white font-bold tracking-wider text-sm">
                Share Profile
              </h2>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="absolute right-6 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </motion.button>
            </div>

            {/* ── Canvas preview ──────────────────────────────────────────── */}
            <div
              className="relative mx-12 rounded-2xl overflow-hidden bg-[#1a1a1a]"
              style={{ aspectRatio: `${CARD_W}/${CARD_H}` }}
            >
              <canvas
                ref={canvasRef}
                width={CARD_W}
                height={CARD_H}
                className="w-full h-full block"
              />
              <AnimatePresence>
                {isRendering && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl"
                  >
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Background selector ─────────────────────────────────────── */}
            <div className="px-12 pt-8 pb-2 flex flex-col items-center">
              <p className="text-white/50 mb-4 font-semibold text-xs tracking-wider uppercase text-center">
                CHOOSE BACKGROUND
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                {BACKGROUNDS.map(bg => (
                  <motion.button
                    key={bg.id}
                    whileTap={{ scale: 0.85 }}
                    onClick={() => handleBgSelect(bg.id as BgId)}
                    title={bg.label}
                    className="relative w-9 h-9 rounded-full focus:outline-none"
                    style={{ background: bg.swatch }}
                  >
                    {selectedBg === bg.id && (
                      <motion.span
                        layoutId="bg-ring"
                        className="absolute inset-0 rounded-full ring-2 ring-white ring-offset-2 ring-offset-[#111]"
                      />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* ── Action buttons ──────────────────────────────────────────── */}
            <div className="px-12 pt-5 pb-8 flex gap-3">
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleCopyLink}
                className="flex-1 h-12 rounded-2xl text-white font-bold tracking-wide disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity"
                style={{
                  background:  'linear-gradient(135deg,#e63939 0%,#a80000 100%)',
                }}
              >
                {linkButtonText || 'COPY PROFILE LINK'}
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleDownload}
                disabled={busy}
                className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 shrink-0 flex items-center justify-center transition-colors disabled:opacity-50"
              >
                <Download className="w-5 h-5 text-white" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    )}
    </AnimatePresence>
  );
}
